import type { ReactNode } from 'react'
import { word } from '../../language/words.js'
import type { SingleProps } from './kinds.js'

/**
 * Een score op een schaal: `slider` en `rating`.
 *
 * **Zonder aanraking is er geen waarde** (ontwerp 6.2). Een schuifregelaar staat in
 * de browser altijd ergens, en dat is precies het wireframe-probleem van W-73E3X:
 * de slider lijkt op 5 te staan terwijl niemand iets heeft gekozen. Daarom staat de
 * regelaar leeg tot de invuller hem aanraakt, met de knoppenrij als bediening en
 * niet een `input type=range` met een verzonnen beginstand.
 *
 * Het bereik zelf komt bij een beoordelingsvraag uit het criterium (R-148) en niet
 * uit de slider; die control is M7. Wat hier staat is de kale schaal 1 tot 9 uit
 * ontwerp 6.2, met `rating` als vijfsterrenvariant. In de aangeboden schermen is
 * `rating` ongebruikt en loopt de score via `slider`.
 *
 * **Toetsenbord: het radiogroep-patroon van de WAI-ARIA APG, en geen slider** (R-139). Een
 * `role="slider"` eist een `aria-valuenow`, en een lege beginstand heeft er geen; dat zou precies
 * de verzonnen beginstand terugbrengen die hierboven is weggehaald. Dus één tabstop voor de hele
 * schaal (het gekozen punt, of het eerste als er nog niets is gekozen), en de pijltjestoetsen
 * verplaatsen de focus en kiezen meteen, met rondwikkelen aan beide uiteinden. Hetzelfde
 * patroon als de schaal van het criterium (`Criterion.tsx`, M8.12).
 */

export interface ScaleProps extends SingleProps {
  readonly soort: 'slider' | 'rating'
}

const RANGE = { slider: 9, rating: 5 } as const

export function Scale({ soort: kind, taal: language, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable }: ScaleProps): ReactNode {
  const maximum = RANGE[kind]
  const chosen = typeof value === 'number' ? value : undefined
  // Roving tabindex: precies één punt in de gewone tabvolgorde, de rest op -1. Zonder dit belooft
  // de rol pijltjesnavigatie die er niet is, en Tabt een toetsenbordgebruiker langs elk punt.
  const tabIndexFor = (point: number): 0 | -1 =>
    chosen === point || (chosen === undefined && point === 1) ? 0 : -1
  return (
    <div
      className="ef-schaal"
      role="radiogroup"
      id={link.id}
      aria-labelledby={link['aria-labelledby']}
      aria-describedby={link['aria-describedby']}
      aria-required={link['aria-required']}
      data-soort={kind}
      onKeyDown={(event) => {
        const NEXT = ['ArrowRight', 'ArrowDown']
        const PREVIOUS = ['ArrowLeft', 'ArrowUp']
        if (!NEXT.includes(event.key) && !PREVIOUS.includes(event.key)) return
        const points = [
          ...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)'),
        ]
        if (points.length === 0) return
        const current = points.indexOf(document.activeElement as HTMLButtonElement)
        const step = NEXT.includes(event.key) ? 1 : -1
        const next = points[(Math.max(current, 0) + step + points.length) % points.length]
        event.preventDefault()
        next?.focus()
        // Een pijltje kiest altijd een ander punt dan het gekozen, dus deze klik wist nooit.
        next?.click()
      }}
    >
      {Array.from({ length: maximum }, (_, place) => place + 1).map((point) => (
        <button
          type="button"
          key={point}
          className="ef-schaal__punt"
          role="radio"
          aria-checked={chosen === point}
          tabIndex={tabIndexFor(point)}
          disabled={!editable}
          // Nog een keer op dezelfde waarde wist hem: anders is een per ongeluk
          // gegeven score niet terug te nemen, en leeg is een geldige stand.
          onClick={() => onValue(chosen === point ? undefined : point)}
        >
          {point}
        </button>
      ))}
      <span className="ef-schaal__stand">
        {chosen === undefined
          ? word('nogGeenKeuze', language)
          : `${chosen} / ${maximum}`}
      </span>
    </div>
  )
}
